/**
 * registerUtils.ts
 *
 * Lightweight helpers for dictionaryKey grouping and meta-threshold handling.
 * Values arrive pre-scaled from the apiserver — no client-side scaling needed.
 * Thresholds come from register.meta (ISA-18.2 setpoints stored in the DB).
 */

export interface RegisterMeta {
  engineeringMin?: number;
  engineeringMax?: number;
  alarmLow?: number;
  alarmLowLow?: number;
  alarmHigh?: number;
  alarmHighHigh?: number;
  deadbandPercent?: number;
  unit?: string;
}

export interface RegisterLike {
  _id: string;
  name: string;
  dictionaryKey?: string;
  value?: number | boolean | string | null;
  unit?: string;
  meta?: RegisterMeta;
  deviceId?: string;
}

/**
 * Group registers by dictionaryKey (lowercased).
 *
 * Groups full register objects by dictionaryKey.
 * Returned items are full register objects, so existing valueMap code continues
 * to work unchanged (each item still has a .name property).
 */
export function groupByDictionaryKey(
  registers: RegisterLike[]
): Record<string, RegisterLike[]> {
  const groups: Record<string, RegisterLike[]> = {};
  for (const r of registers) {
    const key = (r.dictionaryKey || '').toLowerCase();
    if (!key) continue;
    if (!groups[key]) groups[key] = [];
    groups[key].push(r);
  }
  return groups;
}

/**
 * Sum register values from a name→value map for all registers in a group.
 *
 * Sums group values from a name→value map.
 */
export function computeFromMap(
  group: RegisterLike[],
  valueMap: Record<string, number | null>
): number | null {
  const nums = group
    .map(r => valueMap[r.name])
    .filter((v): v is number => typeof v === 'number');
  return nums.length ? nums.reduce((a, b) => a + b, 0) : null;
}

/**
 * Apply ISA-18.2 threshold colouring to a DOM element using register.meta.
 * Falls back to green when no alarm setpoints are configured.
 *
 * Severity levels:
 *   alarmHighHigh / alarmLowLow  →  critical  (#7f1d1d — dark red)
 *   alarmHigh     / alarmLow     →  high       (#d32f2f — red)
 *   normal                       →  ok         (#2e7d32 — green)
 */
export function applyThreshold(
  el: HTMLElement,
  value: number,
  meta?: RegisterMeta
): void {
  el.style.color = '#ffffff';
  el.style.transition = 'background-color 0.4s ease';

  if (!meta) {
    el.style.backgroundColor = '#2e7d32';
    return;
  }

  const { engineeringMin, engineeringMax, alarmHighHigh, alarmHigh, alarmLow, alarmLowLow } = meta;

  if (
    (engineeringMax != null && value > engineeringMax) ||
    (engineeringMin != null && value < engineeringMin) ||
    (alarmHighHigh != null && value >= alarmHighHigh) ||
    (alarmLowLow   != null && value <= alarmLowLow)
  ) {
    el.style.backgroundColor = '#7f1d1d'; // critical
  } else if (
    (alarmHigh != null && value >= alarmHigh) ||
    (alarmLow  != null && value <= alarmLow)
  ) {
    el.style.backgroundColor = '#d32f2f'; // high alarm
  } else {
    el.style.backgroundColor = '#2e7d32'; // normal / ok
  }
}

/**
 * Check whether a value breaches any alarm threshold defined in meta.
 * Returns the severity level, or null if no breach.
 */
export function checkThreshold(
  value: number,
  meta?: RegisterMeta
): 'critical' | 'high' | null {
  if (!meta) return null;
  const { engineeringMin, engineeringMax, alarmHighHigh, alarmHigh, alarmLow, alarmLowLow } = meta;
  if (
    (engineeringMax != null && value > engineeringMax) ||
    (engineeringMin != null && value < engineeringMin) ||
    (alarmHighHigh != null && value >= alarmHighHigh) ||
    (alarmLowLow   != null && value <= alarmLowLow)
  ) return 'critical';
  if (
    (alarmHigh != null && value >= alarmHigh) ||
    (alarmLow  != null && value <= alarmLow)
  ) return 'high';
  return null;
}

/**
 * Format a numeric register value for display.
 */
export function formatRegValue(
  value: number | null | undefined,
  unit?: string,
  decimals = 1
): string {
  if (value == null) return '—';
  return `${value.toFixed(decimals)}${unit ? ' ' + unit : ''}`;
}
